import axios from 'axios';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { API_BASE } from './config';
import { getToken, setAccessToken, clearAuth } from './auth';
import { getDeviceId } from './device';

let onSessionExpired: (() => void) | null = null;
export function registerSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

// Round-10 / H1 phase 2 — DO NOT set withCredentials globally.
// Credentialed CORS is stricter (server must echo the exact Origin and
// Allow-Credentials: true; '*' is forbidden). Setting it on every
// request narrows CORS for endpoints that don't need cookies — every
// endpoint here uses bearer auth in the Authorization header except
// the three /auth/* endpoints that handle the refresh cookie. Opt
// into credentials per-call where the cookie actually matters.
export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  const token = await getToken();
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  try {
    const deviceId = await getDeviceId();
    if (deviceId) (config.headers as any)['X-Device-Id'] = deviceId;
  } catch {
    /* ignore */
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  // Cookie carries the refresh token now. Empty body, withCredentials so
  // the browser attaches the SameSite=Strict + Path-scoped cookie.
  // If the cookie is missing (user mid-session at the phase-2 cutover,
  // never received one), the server returns 401 → we clear local state
  // and the AuthProvider routes to /login. One-time forced re-login.
  try {
    const deviceId = await getDeviceId().catch(() => null);
    const headers: Record<string, string> = {};
    if (deviceId) headers['X-Device-Id'] = deviceId;
    const res = await axios.post(`${API_BASE}/auth/refresh`, {}, { headers, withCredentials: true });
    const access = res.data.access_token as string;
    if (!access) return null;
    await setAccessToken(access);
    return access;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true;
      if (!refreshing) refreshing = doRefresh().finally(() => { refreshing = null; });
      const newToken = await refreshing;
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return api(original);
      }
      await clearAuth();
      onSessionExpired?.();
    }
    return Promise.reject(error);
  },
);
